import { useState, useEffect, useRef } from 'react';
import api from '../../api';
import ChannelSelect from '../../components/ui/ChannelSelect';

// Locked Brazy identity logo — must match backend/utils/constants.js BRAND_LOGO_URL.
// Used as the default for every image field, and as the fixed value for the
// locked (premium-only) Profile Avatar and Footer Logo fields.
const BRAND_LOGO_URL = 'https://i.postimg.cc/y8T0V6vZ/41d1b5a2-fe39-483a-904a-e0dea6683628.png';

const PLACEHOLDERS = [
  { tag: '{user}', desc: 'Mentions the member' },
  { tag: '{username}', desc: 'Their display name' },
  { tag: '{server}', desc: 'Server name' },
  { tag: '{member_count}', desc: 'Total member count' },
];

const TABS = [
  { key: 'welcome', label: 'Welcome', icon: '👋' },
  { key: 'goodbye', label: 'Goodbye', icon: '🚪' },
  { key: 'dm', label: 'Join DM', icon: '✉️' },
  { key: 'autorole', label: 'Auto-Role', icon: '🏷️' },
];

// Per-type config — field names, whether a channel/footer applies, preview title.
const TYPE_CONFIG = {
  welcome: {
    enabledField: 'welcome_enabled',
    channelField: 'welcome_channel_id',
    messageField: 'welcome_message',
    hasChannel: true,
    hasFooter: true,
    hasFooterIconField: true, // only Welcome shows a Footer Logo box
    label: 'Welcome Message',
    previewTitle: guildName => `Welcome to ${guildName}!`,
  },
  goodbye: {
    enabledField: 'goodbye_enabled',
    channelField: 'goodbye_channel_id',
    messageField: 'goodbye_message',
    hasChannel: true,
    hasFooter: true,
    hasFooterIconField: false, // footer icon still shows in preview (locked), just no input box
    label: 'Goodbye Message',
    previewTitle: () => 'Member Left',
  },
  dm: {
    enabledField: 'join_dm_enabled',
    channelField: null,
    messageField: 'join_dm_message',
    hasChannel: false,
    hasFooter: false, // no footer at all for DMs
    hasFooterIconField: false,
    label: 'Join DM',
    previewTitle: guildName => `Welcome to ${guildName}`,
  },
};

const SHAPE_DEF = [
  { id: 'rounded', label: 'Rounded', isDefault: true },
  { id: 'circle', label: 'Circle' },
  { id: 'heart', label: 'Heart' },
  { id: 'collage', label: 'Collage' },
  { id: 'glitchStatic', label: 'Glitch (Static)' },
  { id: 'shatter', label: 'Shatter' },
];

const HEART_CLIP = 'polygon(50.00% 24.91%, 50.03% 24.40%, 50.25% 22.90%, 50.83% 20.55%, 51.92% 17.56%, 53.62% 14.20%, 56.00% 10.76%, 59.06% 7.56%, 62.75% 4.88%, 66.97% 2.96%, 71.58% 2.00%, 76.38% 2.11%, 81.18% 3.32%, 85.73% 5.60%, 89.83% 8.85%, 93.26% 12.92%, 95.85% 17.64%, 97.45% 22.80%, 98.00% 28.23%, 97.45% 33.77%, 95.85% 39.28%, 93.26% 44.67%, 89.83% 49.90%, 85.73% 54.94%, 81.18% 59.79%, 76.38% 64.49%, 71.58% 69.03%, 66.97% 73.44%, 62.75% 77.69%, 59.06% 81.76%, 56.00% 85.57%, 53.62% 89.05%, 51.92% 92.09%, 50.83% 94.59%, 50.25% 96.46%, 50.03% 97.61%, 50.00% 98.00%, 49.97% 97.61%, 49.75% 96.46%, 49.17% 94.59%, 48.08% 92.09%, 46.38% 89.05%, 44.00% 85.57%, 40.94% 81.76%, 37.25% 77.69%, 33.03% 73.44%, 28.42% 69.03%, 23.62% 64.49%, 18.82% 59.79%, 14.27% 54.94%, 10.17% 49.90%, 6.74% 44.67%, 4.15% 39.28%, 2.55% 33.77%, 2.00% 28.23%, 2.55% 22.80%, 4.15% 17.64%, 6.74% 12.92%, 10.17% 8.85%, 14.27% 5.60%, 18.82% 3.32%, 23.62% 2.11%, 28.42% 2.00%, 33.03% 2.96%, 37.25% 4.88%, 40.94% 7.56%, 44.00% 10.76%, 46.38% 14.20%, 48.08% 17.56%, 49.17% 20.55%, 49.75% 22.90%, 49.97% 24.40%)';
const COLLAGE_OVERLAY_CSS = { background: 'linear-gradient(90deg, transparent 0, transparent calc(33.333% - 2px), #000 calc(33.333% - 2px), #000 calc(33.333% + 2px), transparent calc(33.333% + 2px), transparent calc(66.666% - 2px), #000 calc(66.666% - 2px), #000 calc(66.666% + 2px), transparent calc(66.666% + 2px), transparent 100%)' };
const GLITCH_STATIC_OVERLAY_CSS = { background: 'repeating-linear-gradient(0deg, rgba(0,255,255,0.14) 0px, rgba(0,255,255,0.14) 2px, transparent 2px, transparent 4px), linear-gradient(135deg, rgba(255,0,255,0.20), rgba(0,255,255,0.20))' };
// Shatter overlay asset isn't bundled here — falls back to a plain rounded look until
// that asset is added. Everything else (circle/heart/collage/glitch) works today.
const SHATTER_OVERLAY_CSS = { background: 'repeating-linear-gradient(115deg, rgba(255,255,255,0.10) 0px, rgba(255,255,255,0.10) 3px, transparent 3px, transparent 9px)' };

// NOTE: these shapes are cosmetic-preview-only right now. Discord's embed image
// is always a flat rectangle — actually rendering a cropped/composited shape on
// a real send requires the server-side image-generation stage (deferred).
function bannerRender(shape, maxWidth, squareSize, ratio, maxHeight = Infinity) {
  const r = ratio && ratio > 0 ? ratio : 1;
  switch (shape) {
    case 'circle': {
      const circleSize = Math.min(Math.round(squareSize * 1.15), maxHeight);
      return { wrapStyle: { marginTop: '12px', display: 'flex', justifyContent: 'center' }, imgStyle: { width: circleSize, height: circleSize, objectFit: 'cover', clipPath: 'circle(50%)', display: 'block' }, overlay: null };
    }
    case 'heart': {
      const heartSize = Math.min(Math.round(squareSize * 1.3), maxHeight);
      return { wrapStyle: { marginTop: '12px', display: 'flex', justifyContent: 'center' }, imgStyle: { width: heartSize, height: heartSize, objectFit: 'cover', clipPath: HEART_CLIP, display: 'block' }, overlay: null };
    }
    case 'collage': {
      const contentW = maxWidth - 30, h = Math.min(Math.round(contentW / r), maxHeight);
      return { wrapStyle: { marginTop: '10px', position: 'relative' }, imgStyle: { width: '100%', height: h, objectFit: 'cover', borderRadius: '4px', display: 'block' }, overlay: COLLAGE_OVERLAY_CSS };
    }
    case 'glitchStatic': {
      const contentW = maxWidth - 30, h = Math.min(Math.round(contentW / r), maxHeight);
      return { wrapStyle: { marginTop: '10px', position: 'relative', borderRadius: '6px', overflow: 'hidden', boxShadow: '0 0 0 2px #0ff, 0 0 16px 2px #f0f' }, imgStyle: { width: '100%', height: h, objectFit: 'cover', display: 'block', filter: 'contrast(1.2) saturate(1.4) hue-rotate(12deg)' }, overlay: GLITCH_STATIC_OVERLAY_CSS };
    }
    case 'shatter': {
      const contentW = maxWidth - 30, h = Math.min(Math.round(contentW / r), maxHeight);
      return { wrapStyle: { marginTop: '8px', position: 'relative', borderRadius: '4px', overflow: 'hidden' }, imgStyle: { width: '100%', height: h, objectFit: 'cover', display: 'block' }, overlay: SHATTER_OVERLAY_CSS };
    }
    default: {
      // Width-driven height, but capped so a tall/narrow source image can't
      // blow the preview past the visible viewport.
      const contentW = maxWidth - 30, h = Math.min(Math.round(contentW / r), maxHeight);
      return { wrapStyle: { marginTop: '8px' }, imgStyle: { width: '100%', height: h, borderRadius: '4px', objectFit: 'cover', display: 'block' }, overlay: null };
    }
  }
}

function bannerThumb(shape, size) {
  switch (shape) {
    case 'circle': return { w: Math.round(size * 1.15), h: Math.round(size * 1.15), clip: 'circle(50%)' };
    case 'heart': return { w: Math.round(size * 1.3), h: Math.round(size * 1.3), clip: HEART_CLIP };
    case 'collage': return { w: size, h: size, radius: '4px', overlay: COLLAGE_OVERLAY_CSS };
    case 'glitchStatic': return { w: size, h: size, radius: '6px', ring: '0 0 0 2px #0ff, 0 0 10px 1px #f0f', filter: 'contrast(1.2) saturate(1.4) hue-rotate(12deg)', overlay: GLITCH_STATIC_OVERLAY_CSS };
    case 'shatter': return { w: size, h: size, radius: '4px', overlay: SHATTER_OVERLAY_CSS };
    default: return { w: size, h: size, radius: '6px' };
  }
}

function resolvePlainTags(s, guildName) {
  return (s || '').replace(/\{server\}/g, guildName || "Brazyy's Server").replace(/\{member_count\}/g, '1,204');
}
function splitMention(msg, guildName) {
  const m = (msg || '').match(/\{user\}|\{username\}/);
  if (!m) return { before: resolvePlainTags(msg || '', guildName), hasMention: false, mention: '', after: '' };
  const before = resolvePlainTags(msg.slice(0, m.index), guildName);
  const after = resolvePlainTags(msg.slice(m.index + m[0].length), guildName);
  return { before, hasMention: true, mention: '@Brazyy', after };
}

export default function WelcomerPage({ guild }) {
  const [form, setForm] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [tab, setTab] = useState('welcome');
  const [imgRatios, setImgRatios] = useState({ welcome: 1, goodbye: 1, dm: 1 });
  const [themeModalTab, setThemeModalTab] = useState(null); // 'welcome' | 'goodbye' | 'dm' | null
  const [themeSearch, setThemeSearch] = useState('');
  const [testSending, setTestSending] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [dmTestIds, setDmTestIds] = useState('');

  const messageRefs = { welcome: useRef(null), goodbye: useRef(null), dm: useRef(null) };

  const loadRatio = (url, type) => {
    if (!url) return;
    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth && img.naturalHeight) {
        setImgRatios(p => ({ ...p, [type]: img.naturalWidth / img.naturalHeight }));
      }
    };
    img.src = url;
  };

  useEffect(() => {
    Promise.all([
      api.get(`/api/welcomer/${guild.id}`),
      api.get(`/api/welcomer/${guild.id}/roles`).catch(() => ({ data: [] })),
    ]).then(([settings, rolesRes]) => {
      const d = { ...settings.data };
      // Default every image field to the Brazy logo so it loads visibly out of
      // the box — Thumbnail/Banner stay editable, Profile/Footer stay locked.
      ['welcome', 'goodbye', 'dm'].forEach(t => {
        if (!d[`${t}_image_url`]) d[`${t}_image_url`] = BRAND_LOGO_URL;
        if (!d[`${t}_logo_url`]) d[`${t}_logo_url`] = BRAND_LOGO_URL;
        if (!d[`${t}_shape`]) d[`${t}_shape`] = 'rounded';
      });
      d.autorole_ids = Array.isArray(d.autorole_ids) ? d.autorole_ids : JSON.parse(d.autorole_ids || '[]');
      setForm(d);
      setRoles(rolesRes.data);
      ['welcome', 'goodbye', 'dm'].forEach(t => loadRatio(d[`${t}_image_url`], t));
    }).catch(console.error).finally(() => setLoading(false));
  }, [guild.id]);

  const update = (key, val) => setForm(p => ({ ...p, [key]: val }));
  const updateImage = (type, val) => { update(`${type}_image_url`, val); loadRatio(val, type); };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch(`/api/welcomer/${guild.id}`, form);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch { alert('Failed to save.'); }
    finally { setSaving(false); }
  };

  const sendTest = async (type) => {
    setTestSending(type);
    setTestResult(null);
    try {
      let body = {};
      if (type === 'dm') {
        const ids = dmTestIds.split(',').map(s => s.trim()).filter(Boolean);
        if (ids.length) body = { discord_ids: ids };
      }
      const res = await api.post(`/api/welcomer/${guild.id}/test/${type}`, body);
      if (type === 'dm' && res.data.results) {
        const failed = res.data.results.filter(r => !r.success);
        setTestResult({
          type, ok: failed.length === 0,
          message: failed.length
            ? `Sent to ${res.data.results.length - failed.length}/${res.data.results.length}. Failed: ${failed.map(f => f.discord_id).join(', ')}`
            : `Sent to ${res.data.results.length} recipient(s).`,
        });
      } else {
        setTestResult({ type, ok: true, message: 'Test message sent!' });
      }
    } catch (err) {
      setTestResult({ type, ok: false, message: err.response?.data?.error || 'Failed to send test message.' });
    } finally {
      setTestSending(null);
      setTimeout(() => setTestResult(null), 6000);
    }
  };

  const insertPlaceholder = (fieldKey, ref, tag) => {
    const el = ref.current;
    const current = form[fieldKey] || '';
    if (el && typeof el.selectionStart === 'number') {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const next = current.slice(0, start) + tag + current.slice(end);
      update(fieldKey, next);
      setTimeout(() => { el.focus(); el.selectionStart = el.selectionEnd = start + tag.length; }, 0);
    } else {
      update(fieldKey, current + tag);
    }
  };

  const toggleAutorole = (roleId) => {
    const current = form.autorole_ids || [];
    update('autorole_ids', current.includes(roleId) ? current.filter(r => r !== roleId) : [...current, roleId]);
  };

  const inputStyle = {
    width: '100%', padding: '10px 12px', boxSizing: 'border-box',
    border: '2px solid var(--border)', borderRadius: '10px',
    fontSize: '14px', fontFamily: 'var(--font-body)',
    background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none',
  };
  const lockedInputStyle = { ...inputStyle, background: 'var(--bg-secondary)', color: 'var(--text-muted)', cursor: 'not-allowed' };
  const labelStyle = { fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' };
  const cardStyle = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '16px', padding: '24px', boxShadow: 'var(--shadow-card)' };

  const Toggle = ({ value, onChange }) => (
    <button onClick={() => onChange(!value)} style={{
      width: '48px', height: '26px', borderRadius: '13px', border: 'none', cursor: 'pointer',
      background: value ? 'var(--success)' : 'var(--border)',
      position: 'relative', transition: 'background 0.2s', flexShrink: 0,
    }}>
      <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '3px', left: value ? '25px' : '3px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
    </button>
  );

  const PlaceholderChips = ({ fieldKey, inputRef }) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
      <span style={{ fontSize: '11px', color: 'var(--text-muted)', alignSelf: 'center' }}>Insert:</span>
      {PLACEHOLDERS.map(p => (
        <button key={p.tag} onClick={() => insertPlaceholder(fieldKey, inputRef, p.tag)} title={p.desc}
          style={{ fontSize: '11px', fontFamily: 'monospace', padding: '3px 10px', borderRadius: '100px', background: 'var(--accent-soft)', color: 'var(--accent-primary)', fontWeight: 600, border: 'none', cursor: 'pointer' }}>
          {p.tag}
        </button>
      ))}
    </div>
  );

  const TestSendButton = ({ type, label }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
      <button onClick={() => sendTest(type)} disabled={testSending === type} style={{
        padding: '10px 16px', borderRadius: '10px', border: 'none', cursor: testSending === type ? 'default' : 'pointer',
        background: 'var(--accent-primary)', color: '#fff', fontWeight: 700, fontSize: '13px', opacity: testSending === type ? 0.7 : 1,
      }}>
        {testSending === type ? 'Sending…' : `📨 ${label}`}
      </button>
      {testResult && testResult.type === type && (
        <span style={{ fontSize: '12px', fontWeight: 600, color: testResult.ok ? 'var(--success)' : '#E8341C' }}>
          {testResult.ok ? '✓' : '✕'} {testResult.message}
        </span>
      )}
    </div>
  );

  if (loading || !form) return (
    <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
      <div style={{ fontSize: '40px', marginBottom: '12px' }}>⏳</div>Loading welcomer settings...
    </div>
  );

  // ---- Shape modal ----
  const ThemeModal = () => {
    if (!themeModalTab) return null;
    const type = themeModalTab;
    const currentShape = form[`${type}_shape`] || 'rounded';
    const color = form[`${type}_color`] || '#E8341C';
    const image = form[`${type}_image_url`];
    const filtered = SHAPE_DEF.filter(o => o.label.toLowerCase().includes(themeSearch.toLowerCase()));

    return (
      <div onClick={() => { setThemeModalTab(null); setThemeSearch(''); }} style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-card)', borderRadius: '20px', padding: '28px', maxWidth: '780px', width: '92%', maxHeight: '86vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', color: 'var(--text-primary)', marginBottom: '6px' }}>🎭 Discover Themes</h3>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '4px' }}>Pick how the banner image frames in this section's embed.</p>
          <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '16px', fontStyle: 'italic' }}>
            Preview only for now — real sends stay rectangular until image generation ships.
          </p>
          <input type="text" value={themeSearch} onChange={e => setThemeSearch(e.target.value)} placeholder="Search themes..."
            style={{ width: '100%', padding: '10px 14px', border: '2px solid var(--border)', borderRadius: '100px', fontSize: '13px', fontFamily: 'var(--font-body)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', marginBottom: '16px', boxSizing: 'border-box' }} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
            {filtered.map(opt => {
              const t = bannerThumb(opt.id, 130);
              const selected = currentShape === opt.id;
              const bg = image ? { backgroundImage: `url('${image}')`, backgroundSize: 'cover', backgroundPosition: 'center' } : { background: `linear-gradient(135deg, ${color}, #F5A623)` };
              return (
                <button key={opt.id} onClick={() => { update(`${type}_shape`, opt.id); setThemeModalTab(null); setThemeSearch(''); }} style={{
                  padding: '16px 10px', borderRadius: '14px', cursor: 'pointer',
                  border: `2px solid ${selected ? 'var(--accent-primary)' : 'var(--border)'}`,
                  background: selected ? 'var(--accent-soft)' : 'var(--bg-secondary)',
                  fontFamily: 'var(--font-body)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px',
                }}>
                  <div style={{ position: 'relative', width: t.w, height: t.h, borderRadius: t.radius || 0, overflow: 'hidden', clipPath: t.clip, boxShadow: t.ring }}>
                    <div style={{ position: 'absolute', inset: 0, filter: t.filter, ...bg }} />
                    {t.overlay && <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', ...t.overlay }} />}
                  </div>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: selected ? 'var(--accent-primary)' : 'var(--text-secondary)', textAlign: 'center' }}>{opt.label}</div>
                  {opt.isDefault && <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: 'var(--text-muted)' }}>Default</div>}
                </button>
              );
            })}
          </div>
          {filtered.length === 0 && <p style={{ textAlign: 'center', fontSize: '13px', color: 'var(--text-muted)', padding: '24px 0' }}>No themes match "{themeSearch}".</p>}
        </div>
      </div>
    );
  };

  // ---- Live preview ----
  const DiscordPreview = ({ type }) => {
    const cfg = TYPE_CONFIG[type];
    const color = form[`${type}_color`] || '#E8341C';
    const shape = form[`${type}_shape`] || 'rounded';
    const bannerUrl = form[`${type}_image_url`];
    const thumbUrl = form[`${type}_logo_url`];
    const footerText = form[`${type}_footer_text`] || "Brazyy's Server";
    const { before, mention, after, hasMention } = splitMention(form[cfg.messageField], guild.name);
    // Enlarged preview — width matches the card's real (larger) width instead
    // of Discord's actual ~432px cap, but banner height is capped so a tall
    // source image can't push the whole card past the visible viewport.
    const CARD_MAX_WIDTH = 720;
    const BANNER_MAX_HEIGHT = 260;
    const banner = bannerRender(shape, CARD_MAX_WIDTH, 240, imgRatios[type], BANNER_MAX_HEIGHT);
    // Thumbnail is always a plain square, regardless of shape —
    // confirmed against the mockup source. Shape only ever affects the banner.
    // Scaled up to match the larger card.

    return (
      <div style={{ position: 'sticky', top: '80px', maxHeight: 'calc(100vh - 100px)', overflow: 'hidden', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ width: '100%', maxWidth: `${CARD_MAX_WIDTH}px`, fontSize: '12px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>
          👁️ Live Preview {type === 'dm' ? '(sent privately)' : ''}
        </div>
        <div style={{ background: '#313338', borderRadius: '12px', padding: '20px', width: '100%', maxWidth: `${CARD_MAX_WIDTH}px` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            <div style={{ width: '46px', height: '46px', borderRadius: '50%', backgroundImage: `url('${BRAND_LOGO_URL}')`, backgroundSize: 'cover', backgroundPosition: 'center', flexShrink: 0 }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ color: '#f2c14e', fontSize: '17px', fontWeight: 600 }}>Brazyy's Clock-In</span>
              <span style={{ background: '#5865F2', color: '#fff', fontSize: '11px', fontWeight: 700, padding: '2px 5px', borderRadius: '3px' }}>APP</span>
              <span style={{ color: '#949ba4', fontSize: '13px' }}>Today at {new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>
            </div>
          </div>

          {hasMention && (
            <div style={{ margin: '2px 0 10px' }}>
              <span style={{ background: 'rgba(88,101,242,0.3)', color: '#c9cdfb', borderRadius: '3px', padding: '0 3px', fontSize: '17px' }}>{mention}</span>
            </div>
          )}

          <div style={{ display: 'flex' }}>
            <div style={{ width: '5px', flexShrink: 0, borderRadius: '3px 0 0 3px', background: color }} />
            <div style={{ flex: 1, minWidth: 0, background: '#2b2d31', borderRadius: '0 8px 8px 0', padding: '16px 18px' }}>
              <div style={{ display: 'flex', gap: '18px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: '#fff', fontSize: '19px', fontWeight: 600, marginBottom: '8px' }}>{cfg.previewTitle(guild.name)}</div>
                  <div style={{ color: '#dbdee1', fontSize: '15px', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                    {before}
                    {hasMention && <span style={{ background: 'rgba(88,101,242,0.3)', color: '#c9cdfb', borderRadius: '3px', padding: '0 3px' }}>{mention}</span>}
                    {after}
                  </div>
                </div>
                {thumbUrl && (
                  <div role="img" style={{ width: '100px', height: '100px', borderRadius: '8px', backgroundImage: `url('${thumbUrl}')`, backgroundSize: 'cover', backgroundPosition: 'center', flexShrink: 0 }} />
                )}
              </div>

              {bannerUrl && (
                <div style={banner.wrapStyle}>
                  <div style={{ position: 'relative', ...banner.imgStyle }}>
                    <div style={{ position: 'absolute', inset: 0, backgroundImage: `url('${bannerUrl}')`, backgroundSize: 'cover', backgroundPosition: 'center', ...banner.imgStyle }} />
                    {banner.overlay && <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', ...banner.overlay }} />}
                  </div>
                </div>
              )}

              {cfg.hasFooter && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '14px' }}>
                  <div style={{ width: '22px', height: '22px', borderRadius: '50%', backgroundImage: `url('${BRAND_LOGO_URL}')`, backgroundSize: 'cover', backgroundPosition: 'center', flexShrink: 0 }} />
                  <span style={{ color: '#949ba4', fontSize: '13px' }}>{footerText}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ---- Shared per-type tab body (Welcome / Goodbye / DM) ----
  const MessageTab = ({ type }) => {
    const cfg = TYPE_CONFIG[type];
    const enabled = form[cfg.enabledField];
    return (
      <div className="wc-welcomer-2col" style={{ gap: '24px', alignItems: 'start' }}>
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', color: 'var(--text-primary)' }}>{cfg.label}</h2>
            <Toggle value={enabled} onChange={v => update(cfg.enabledField, v)} />
          </div>
          {enabled && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {cfg.hasChannel && (
                <div>
                  <label style={labelStyle}>Channel</label>
                  <ChannelSelect guildId={guild.id} value={form[cfg.channelField]} onChange={v => update(cfg.channelField, v)} placeholder="Select channel..." />
                </div>
              )}
              <div>
                <label style={labelStyle}>Message</label>
                <textarea ref={messageRefs[type]} value={form[cfg.messageField] || ''} onChange={e => update(cfg.messageField, e.target.value)}
                  rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
                <PlaceholderChips fieldKey={cfg.messageField} inputRef={messageRefs[type]} />
              </div>

              <div>
                <label style={labelStyle}>Banner Image or GIF</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input type="url" value={form[`${type}_image_url`] || ''} onChange={e => updateImage(type, e.target.value)}
                    placeholder="https://i.postimg.cc/.../banner.gif" style={inputStyle} />
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                  Discord displays banners up to ~400px wide. Wide formats (1200×400) look best. Paste a URL for now — direct file upload is coming in a follow-up.
                </div>
              </div>

              <div>
                <label style={labelStyle}>Thumbnail</label>
                <input type="url" value={form[`${type}_logo_url`] || ''} onChange={e => update(`${type}_logo_url`, e.target.value)}
                  placeholder="https://i.postimg.cc/.../thumbnail.png" style={inputStyle} />
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                  Shows as a small image in the top-right of the embed.
                </div>
              </div>

              <div>
                <label style={labelStyle}>Color</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input type="color" value={form[`${type}_color`] || '#E8341C'} onChange={e => update(`${type}_color`, e.target.value)}
                    style={{ width: '44px', height: '40px', border: '2px solid var(--border)', borderRadius: '8px', cursor: 'pointer', background: 'none' }} />
                  <input type="text" value={form[`${type}_color`] || '#E8341C'} onChange={e => update(`${type}_color`, e.target.value)}
                    style={{ ...inputStyle, fontFamily: 'monospace' }} />
                </div>
              </div>

              {cfg.hasFooter && (
                <div>
                  <label style={labelStyle}>Footer Text</label>
                  <input type="text" value={form[`${type}_footer_text`] || ''} onChange={e => update(`${type}_footer_text`, e.target.value)}
                    placeholder="Brazyy's Server" style={inputStyle} />
                </div>
              )}

              {/* Premium — locked fields, grouped together below the free ones */}
              <div style={{ marginTop: '4px', paddingTop: '16px', borderTop: '1px dashed var(--border)' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
                  🔒 Premium — Coming Soon
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div>
                    <label style={labelStyle}>Bot Name</label>
                    <input type="text" value="Brazyy's Clock-In" disabled style={lockedInputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Profile Avatar</label>
                    <input type="url" value={BRAND_LOGO_URL} disabled style={lockedInputStyle} />
                  </div>
                  {cfg.hasFooterIconField && (
                    <div>
                      <label style={labelStyle}>Footer Logo</label>
                      <input type="url" value={BRAND_LOGO_URL} disabled style={lockedInputStyle} />
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
                <button onClick={() => setThemeModalTab(type)} style={{
                  flex: 1, padding: '11px 14px', borderRadius: '100px', cursor: 'pointer', border: '2px solid var(--accent-primary)',
                  background: 'var(--accent-soft)', color: 'var(--accent-primary)', fontFamily: 'var(--font-display)', fontSize: '13px', fontWeight: 700,
                }}>🎭 Discover Themes</button>
              </div>

              <div style={{ borderTop: '1px solid var(--border)', paddingTop: '14px' }}>
                {type === 'dm' && (
                  <div style={{ marginBottom: '10px' }}>
                    <label style={labelStyle}>Test recipient Discord ID(s) — optional</label>
                    <input type="text" value={dmTestIds} onChange={e => setDmTestIds(e.target.value)}
                      placeholder="Leave blank to DM yourself, or paste ID(s) comma-separated" style={inputStyle} />
                  </div>
                )}
                <TestSendButton type={type} label={type === 'dm' ? 'Send Test DM' : 'Send Test Message'} />
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '6px' }}>
                  Sends your last-saved settings now. Save first if you've made changes.
                </div>
              </div>
            </div>
          )}
        </div>
        {enabled ? <DiscordPreview type={type} /> : <div style={{ color: 'var(--text-muted)', fontSize: '13px', padding: '20px' }}>Enable to see the live preview.</div>}
      </div>
    );
  };

  return (
    <div>
      <style>{`
        .wc-welcomer-2col { display: grid; grid-template-columns: 1fr 1fr; }
        @media (max-width: 900px) { .wc-welcomer-2col { grid-template-columns: 1fr; } }
      `}</style>
      <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '32px', color: 'var(--text-primary)', marginBottom: '6px' }}>Welcomer</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '15px' }}>Greet new members and see it live before you save</p>
        </div>
        <button onClick={handleSave} disabled={saving} style={{
          padding: '12px 28px', background: saved ? 'var(--success)' : 'var(--gradient-accent)',
          color: '#fff', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: 700,
          cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-display)', boxShadow: 'var(--shadow)',
        }}>
          {saving ? 'Saving...' : saved ? '✓ Saved!' : 'Save Changes'}
        </button>
      </div>

      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', background: 'var(--bg-secondary)', borderRadius: '12px', padding: '4px', width: 'fit-content', flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '9px 18px', borderRadius: '8px', cursor: 'pointer', border: 'none',
            fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: 600,
            background: tab === t.key ? 'var(--bg-card)' : 'transparent',
            color: tab === t.key ? 'var(--text-primary)' : 'var(--text-muted)',
            boxShadow: tab === t.key ? 'var(--shadow-card)' : 'none', transition: 'all 0.15s',
          }}>{t.icon} {t.label}</button>
        ))}
      </div>

      {tab === 'welcome' && <MessageTab type="welcome" />}
      {tab === 'goodbye' && <MessageTab type="goodbye" />}
      {tab === 'dm' && <MessageTab type="dm" />}

      {tab === 'autorole' && (
        <div style={{ maxWidth: '620px' }}>
          <div style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', color: 'var(--text-primary)' }}>Auto-Role</h2>
              <Toggle value={form.autorole_enabled} onChange={v => update('autorole_enabled', v)} />
            </div>
            {form.autorole_enabled && (
              <div>
                <label style={labelStyle}>Roles assigned immediately on join</label>
                {roles.length === 0 ? (
                  <div style={{ fontSize: '13px', color: 'var(--text-muted)', padding: '10px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                    No roles found. Make sure the bot's role is high enough in the role list.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {roles.map(r => {
                      const selected = (form.autorole_ids || []).includes(r.id);
                      return (
                        <div key={r.id} onClick={() => toggleAutorole(r.id)} style={{
                          padding: '6px 14px', borderRadius: '100px', cursor: 'pointer',
                          border: `2px solid ${selected ? 'var(--accent-primary)' : 'var(--border)'}`,
                          background: selected ? 'var(--accent-soft)' : 'var(--bg-secondary)',
                          fontSize: '13px', fontWeight: 600,
                          color: selected ? 'var(--accent-primary)' : 'var(--text-secondary)',
                          display: 'flex', alignItems: 'center', gap: '6px',
                        }}>
                          {r.color ? <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: `#${r.color.toString(16).padStart(6, '0')}` }} /> : null}
                          {r.name}{selected && <span>✓</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '10px' }}>
                  ⚠️ The bot's own role must be positioned <strong>above</strong> any role it assigns, in Server Settings → Roles.
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <ThemeModal />
      </div>
    </div>
  );
}
